import { useState, useCallback } from 'react';
import { manufacturingAPI } from '../../../services/api';

export const useBOMOperations = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Load a complete BOM tree for an item using the with_children endpoint
   * Returns nodes and edges for React Flow
   */
  const loadBOMTree = useCallback(async (itemId) => {
    setLoading(true);
    setError(null);

    try {
      const rootResponse = await manufacturingAPI.items.get(itemId);
      const rootItem = rootResponse.data;

      const bomResponse = await manufacturingAPI.bom.withChildren({
        search: rootItem.item_code,
        page_size: 500,
      });

      const allBoms = bomResponse.data.all_boms || [];
      
      console.log(`Loaded ${allBoms.length} BOM relationships`);

      const itemsMap = new Map();
      
      itemsMap.set(rootItem.id, {
        id: rootItem.id,
        item_code: rootItem.item_code,
        name: rootItem.name,
        item_type: rootItem.item_type,
        is_phantom: rootItem.is_phantom,
        description: rootItem.description || '',
      });

      allBoms.forEach(bom => {
        if (!itemsMap.has(bom.parent_item)) {
          const [code, ...nameParts] = bom.parent_item_display.split(' - ');
          itemsMap.set(bom.parent_item, {
            id: bom.parent_item,
            item_code: code,
            name: nameParts.join(' - '),
            item_type: 'manufactured',
            is_phantom: false,
            description: '',
          });
        }
        
        if (!itemsMap.has(bom.child_item)) {
          const [code, ...nameParts] = bom.child_item_display.split(' - ');
          itemsMap.set(bom.child_item, {
            id: bom.child_item,
            item_code: code,
            name: nameParts.join(' - '),
            item_type: bom.child_item_type || 'manufactured',
            is_phantom: bom.child_is_phantom || false,
            description: '',
          });
        }
      });

      const connectedItemIds = new Set([rootItem.id]);
      let changed = true;
      
      while (changed) {
        changed = false;
        allBoms.forEach(bom => {
          if (connectedItemIds.has(bom.parent_item) && !connectedItemIds.has(bom.child_item)) {
            connectedItemIds.add(bom.child_item);
            changed = true;
          }
        });
      }

      const nodes = [];
      connectedItemIds.forEach(itemId => {
        const item = itemsMap.get(itemId);
        if (item) {
          nodes.push({
            id: `node-${item.id}`,
            type: 'bomItem',
            position: { x: 0, y: 0 },
            data: {
              itemId: item.id,
              itemCode: item.item_code,
              itemName: item.name,
              itemType: item.item_type,
              isPhantom: item.is_phantom,
              isPending: false,
              description: item.description || '',
            },
          });
        }
      });

      const edges = [];
      const seenEdges = new Set();
      
      allBoms.forEach(bom => {
        if (!connectedItemIds.has(bom.parent_item) || !connectedItemIds.has(bom.child_item)) {
          return;
        }

        const edgeKey = bom.id ? `bom-${bom.id}` : `bom-${bom.parent_item}-${bom.child_item}`;
        
        if (seenEdges.has(edgeKey)) {
          return;
        }
        seenEdges.add(edgeKey);

        edges.push({
          id: edgeKey,
          source: `node-${bom.child_item}`,
          target: `node-${bom.parent_item}`,
          type: 'bomEdge',
          data: {
            quantity: parseFloat(bom.quantity_required) || 1,
            isPhantomPassthrough: bom.child_is_phantom || false,
            bomId: bom.id,
            sequence: bom.sequence,
            consumedInStep: bom.consumed_in_step,
          },
        });
      });

      console.log(`Built tree: ${nodes.length} nodes, ${edges.length} edges`);
      
      return { nodes, edges };
    } catch (err) {
      console.error('Failed to load BOM tree:', err);
      setError(err.message || 'Failed to load BOM');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Save BOM changes including creating new pending items and updating existing items
   * @param {Object} diff - The calculated diff of changes
   * @param {Array} pendingItems - New items to create
   * @param {Array} modifiedItems - Existing items that were modified
   * @param {number|string} userId - The current user's ID for created_by field
   */
  const saveBOMChanges = useCallback(async (diff, pendingItems = [], modifiedItems = [], userId = null) => {
    setLoading(true);
    setError(null);

    try {
      const results = {
        itemsCreated: 0,
        itemsUpdated: 0,
        created: 0,
        updated: 0,
        deleted: 0,
        errors: [],
        itemIdMap: {},
      };

      // STEP 1: Create all pending items first
      console.log(`Creating ${pendingItems.length} pending items...`);
      
      for (const pendingItem of pendingItems) {
        try {
          const response = await manufacturingAPI.items.create({
            item_code: pendingItem.item_code,
            name: pendingItem.name,
            description: pendingItem.description || 'Created via BOM Configurator',
            item_type: pendingItem.item_type,
            is_phantom: pendingItem.is_phantom,
            unit_of_measure: 'EA',
            ...(userId && { created_by: userId }),
          });
          
          results.itemIdMap[pendingItem.id] = response.data.id;
          results.itemsCreated++;
          console.log(`Created item: ${pendingItem.item_code} -> ID ${response.data.id}`);
        } catch (err) {
          console.error('Full error response:', err.response?.data);
          // Try to extract meaningful error from various Django error formats
          let errorDetail = err.message;
          if (err.response?.data) {
            const data = err.response.data;
            if (typeof data === 'string') {
              errorDetail = data;
            } else if (data.detail) {
              errorDetail = data.detail;
            } else if (data.non_field_errors) {
              errorDetail = data.non_field_errors.join(', ');
            } else {
              // Field-specific errors
              const fieldErrors = Object.entries(data)
                .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`)
                .join('; ');
              errorDetail = fieldErrors || JSON.stringify(data);
            }
          }
          const errorMsg = `Failed to create item ${pendingItem.item_code}: ${errorDetail}`;
          results.errors.push(errorMsg);
          console.error(errorMsg);
        }
      }

      // STEP 2: Update all modified existing items
      console.log(`Updating ${modifiedItems.length} modified items...`);
      
      for (const modifiedItem of modifiedItems) {
        try {
          await manufacturingAPI.items.update(modifiedItem.id, {
            item_code: modifiedItem.item_code,
            name: modifiedItem.name,
            description: modifiedItem.description || '',
            item_type: modifiedItem.item_type,
            is_phantom: modifiedItem.is_phantom,
          });
          
          results.itemsUpdated++;
          console.log(`Updated item: ${modifiedItem.item_code} (ID ${modifiedItem.id})`);
        } catch (err) {
          const errorMsg = `Failed to update item ${modifiedItem.item_code}: ${err.response?.data?.item_code?.[0] || err.response?.data?.detail || err.message}`;
          results.errors.push(errorMsg);
          console.error(errorMsg);
        }
      }

      // Helper to resolve item ID (pending -> real, or keep as-is)
      const resolveItemId = (itemId) => {
        if (typeof itemId === 'string' && itemId.startsWith('pending-')) {
          return results.itemIdMap[itemId] || itemId;
        }
        return itemId;
      };

      // STEP 3: Process deletes
      console.log(`Processing ${diff?.deleted?.length || 0} deletes...`);
      
      for (const deleteItem of (diff?.deleted || [])) {
        try {
          if (deleteItem.bomId) {
            await manufacturingAPI.bom.delete(deleteItem.bomId);
            results.deleted++;
            console.log(`Deleted BOM: ${deleteItem.bomId}`);
          }
        } catch (err) {
          results.errors.push(`Delete failed for ${deleteItem.childCode} -> ${deleteItem.parentCode}: ${err.message}`);
        }
      }

      // STEP 4: Process creates (with resolved IDs)
      console.log(`Processing ${diff?.created?.length || 0} creates...`);
      
      for (const createItem of (diff?.created || [])) {
        try {
          const parentId = resolveItemId(createItem.parentId);
          const childId = resolveItemId(createItem.childId);
          
          if (typeof parentId === 'string' && parentId.startsWith('pending-')) {
            results.errors.push(`Cannot create BOM: parent item ${createItem.parentCode} was not created`);
            continue;
          }
          if (typeof childId === 'string' && childId.startsWith('pending-')) {
            results.errors.push(`Cannot create BOM: child item ${createItem.childCode} was not created`);
            continue;
          }

          await manufacturingAPI.bom.create({
            parent_item: parentId,
            child_item: childId,
            quantity_required: createItem.quantity,
            sequence: createItem.sequence || 1,
            is_active: true,
            effective_date: new Date().toISOString().split('T')[0],
            notes: 'Created via BOM Configurator',
          });
          results.created++;
          console.log(`Created BOM: ${createItem.childCode} -> ${createItem.parentCode} (qty: ${createItem.quantity})`);
        } catch (err) {
          const errorDetail = err.response?.data?.non_field_errors?.[0] || 
                             err.response?.data?.detail || 
                             err.message;
          results.errors.push(`Create BOM failed (${createItem.childCode} -> ${createItem.parentCode}): ${errorDetail}`);
        }
      }

      // STEP 5: Process updates
      console.log(`Processing ${diff?.updated?.length || 0} updates...`);
      
      for (const updateItem of (diff?.updated || [])) {
        try {
          await manufacturingAPI.bom.update(updateItem.bomId, {
            quantity_required: updateItem.quantity,
          });
          results.updated++;
          console.log(`Updated BOM ${updateItem.bomId}: qty ${updateItem.oldQuantity} -> ${updateItem.quantity}`);
        } catch (err) {
          results.errors.push(`Update failed for ${updateItem.childCode} -> ${updateItem.parentCode}: ${err.message}`);
        }
      }

      console.log('Save complete:', results);
      return results;
    } catch (err) {
      console.error('Failed to save BOM changes:', err);
      setError(err.message || 'Failed to save changes');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Validate BOM structure
   */
  const validateBOM = useCallback((nodes, edges) => {
    const errors = [];
    const warnings = [];

    edges.forEach(edge => {
      if (edge.source === edge.target) {
        errors.push(`Self-reference detected`);
      }
    });

    edges.forEach(edge => {
      if ((edge.data?.quantity || 0) <= 0) {
        errors.push(`Invalid quantity on an edge`);
      }
    });

    const bomPairs = new Set();
    edges.forEach(edge => {
      const sourceNode = nodes.find(n => n.id === edge.source);
      const targetNode = nodes.find(n => n.id === edge.target);
      if (sourceNode && targetNode) {
        const pairKey = `${sourceNode.data.itemId}->${targetNode.data.itemId}`;
        if (bomPairs.has(pairKey)) {
          errors.push(`Duplicate BOM entry: ${sourceNode.data.itemCode} -> ${targetNode.data.itemCode}`);
        }
        bomPairs.add(pairKey);
      }
    });

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  }, []);

  return {
    loadBOMTree,
    saveBOMChanges,
    validateBOM,
    loading,
    error,
  };
};